import React, { useEffect, useState } from "react";
import { ReasonDialog } from "@/components/BookingStatus";
import { SpotCat } from "@/hooks/useSpotCategories";
import { toast } from "sonner";
import { Check, X, MapPin, ExternalLink } from "lucide-react";
import { imgSrc } from "@/lib/img";
import { api, errorText } from "@/lib/api";
import type { Spot } from "@/lib/spots";
import { usePrefs } from "@/contexts/PrefsContext";

export type Suggestion = Omit<Spot, "spot_id" | "directions_url"> & { suggestion_id: string; status: "pending" | "approved" | "rejected"; author: string; reject_reason?: string; spot_id?: string; created_at: string };
type St = Suggestion["status"];

const SuggestionRow = ({ x, canEdit, act }: { x: Suggestion; canEdit: boolean; act: (x: Suggestion, a: "approve" | "reject") => void }) => {
  const { t } = usePrefs();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`admin-suggestion-${x.suggestion_id}`} className="rounded-2xl border bg-card min-w-0">
      <div className="p-2.5 flex items-center gap-3">
        <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`admin-suggestion-toggle-${x.suggestion_id}`} className="flex-1 min-w-0 flex items-center gap-3 text-start">
          {x.gallery[0] ? <img src={imgSrc(x.gallery[0])} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" /> : <span className="h-14 w-14 shrink-0 rounded-xl bg-emerald-500/10 grid place-items-center"><MapPin className="h-5 w-5 text-emerald-600" /></span>}
          <span className="min-w-0">
            <b className="block truncate">{x.name_ar}</b>
            <span className="block text-xs text-muted-foreground truncate"><SpotCat slug={x.category} /> · {t(`gov.${x.governorate}`)}</span>
            <span className="block text-[11px] text-muted-foreground truncate">{t("sug.by")} {x.author}</span>
          </span>
        </button>
        {canEdit && x.status === "pending" && <div className="flex gap-1.5 shrink-0">
          <button data-testid={`admin-suggestion-approve-${x.suggestion_id}`} onClick={() => act(x, "approve")} title={t("rev.approve")} aria-label={t("rev.approve")} className="h-9 w-9 grid place-items-center rounded-full bg-emerald-600 text-white"><Check className="h-4 w-4" /></button>
          <button data-testid={`admin-suggestion-reject-${x.suggestion_id}`} onClick={() => act(x, "reject")} title={t("rev.reject")} aria-label={t("rev.reject")} className="h-9 w-9 grid place-items-center rounded-full bg-primary/10 text-primary"><X className="h-4 w-4" /></button>
        </div>}
      </div>
      {x.reject_reason && <p className="px-3 pb-2 text-xs text-rose-600">{t("rev.reason")}: {x.reject_reason}</p>}
      {open && (
        <div data-testid={`admin-suggestion-details-${x.suggestion_id}`} className="border-t px-3 py-2.5 space-y-2 text-xs">
          {x.area && <p className="text-muted-foreground">{x.area}</p>}
          {x.hours_ar && <p>{x.hours_ar}</p>}
          {x.description_ar && <p className="text-sm whitespace-pre-line line-clamp-5">{x.description_ar}</p>}
          <a href={`https://www.google.com/maps?q=${x.lat},${x.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-primary"><MapPin className="h-3.5 w-3.5" />{t("admin.maps")}<ExternalLink className="h-3 w-3" /></a>
          {x.gallery.length > 1 && <div className="flex gap-2 overflow-x-auto no-scrollbar">{x.gallery.map((g) => <img key={g} src={imgSrc(g)} alt="" className="h-16 w-24 shrink-0 rounded-lg object-cover" />)}</div>}
        </div>
      )}
    </div>
  );
};

export const AdminSpotSuggestions = ({ canEdit, onApproved }: { canEdit: boolean; onApproved: () => void }) => {
  const { t } = usePrefs();
  const [st, setSt] = useState<St>("pending");
  const [rows, setRows] = useState<Suggestion[] | null>(null);
  const load = () => api.get("/admin/spot-suggestions", { params: { status: st } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    let live = true;
    setRows(null);
    api.get("/admin/spot-suggestions", { params: { status: st } }).then((r) => live && setRows(r.data)).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [st]);

  const [rejecting, setRejecting] = useState<Suggestion | null>(null);
  const act = async (x: Suggestion, a: "approve" | "reject", reason = "") => {
    if (a === "reject" && !reason) return setRejecting(x);
    try {
      await api.post(`/admin/spot-suggestions/${x.suggestion_id}/${a}`, { reason });
      toast.success(t(a === "approve" ? "sug.approved" : "sug.rejected"));
      setRejecting(null);
      load();
      if (a === "approve") onApproved();
    } catch (e) { toast.error(errorText(e, t)); }
  };

  return (
    <div data-testid="admin-spot-suggestions" className="space-y-4">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {(["pending", "approved", "rejected"] as St[]).map((k) => (
          <button key={k} data-testid={`admin-suggestions-filter-${k}`} aria-pressed={st === k} onClick={() => setSt(k)}
            className={`h-9 px-4 rounded-full text-sm font-bold ${st === k ? "bg-foreground text-background" : "bg-card border"}`}>{t(`sug.status.${k}`)}</button>
        ))}
      </div>
      {rows?.length === 0 && <p data-testid="admin-suggestions-empty" className="text-center text-muted-foreground py-12">{t("sug.adminEmpty")}</p>}
      <div className="grid gap-2 lg:grid-cols-2 items-start">
        {rows?.map((x) => <SuggestionRow key={x.suggestion_id} x={x} canEdit={canEdit} act={act} />)}
      </div>
      {rejecting && <ReasonDialog testid="admin-suggestion-reject-dialog" title={t("rev.reject")} note={rejecting.name_ar} onClose={() => setRejecting(null)} onSubmit={(r) => act(rejecting, "reject", r)} />}
    </div>
  );
};
